// National → Puget Sound material terminology.
//
// Almost nobody in Western Washington searches "#57 stone" — but people who
// moved here, followed a national how-to, or were handed an out-of-state spec
// do. Those customers arrive with the wrong words for the right material, and
// a yard that can't translate loses the order. This table is the translation
// layer: it captures the national query without diluting the local vocabulary
// the rest of the site is built on.
//
// `localSlug` must match a product slug in data/products.ts — the display name
// and link are resolved from the catalog so this list can't drift out of sync.
export interface MaterialTranslation {
  /** What the customer arrives asking for. */
  national: string[]
  /** Catalog slug of the closest local equivalent. */
  localSlug: string
  /** What's actually different, in one sentence. */
  note: string
}

export const MATERIAL_TRANSLATIONS: MaterialTranslation[] = [
  {
    national: ['#57 stone', '#57 gravel', 'Washed stone'],
    localSlug: '7-8-drain-rock',
    note: 'The national drainage standard — washed, angular, no fines. Ours is sized 7/8" and does the same job. For a smaller clean stone, use 5/8" Crushed Clear.',
  },
  {
    national: ['3/4 crushed stone', '3/4 clean', '#67 stone'],
    localSlug: '5-8-clear',
    note: 'Same washed, angular, no-fines rock. Puget Sound yards call it 5/8" Crushed Clear, and we stock the 5/8"–3/4" gradation as one material.',
  },
  {
    national: ['Crusher run', 'Dense grade', 'Quarry process (QP)', '#411'],
    localSlug: '5-8-minus',
    note: 'Crushed rock with the fines left in so it compacts hard. On Washington plans it is CSTC — crushed surfacing top course. This is the driveway surface here.',
  },
  {
    national: ['Class 5', 'Road base', '21AA', 'AB-3'],
    localSlug: '1-25-minus',
    note: 'The coarse structural base course under a driveway or pad. Called CSBC locally, and it goes under 5/8" Minus rather than on top.',
  },
  {
    national: ['#8 stone', '#89 stone', 'Chip stone'],
    localSlug: '5-8-clear',
    note: 'A small washed stone with no fines. Our nearest equivalent is 5/8" Crushed Clear — drainage, pipe bedding, and clean landscape rock.',
  },
  {
    national: ['Pea stone', "Bird's eye gravel", '3/8 round'],
    localSlug: 'pea-gravel',
    note: 'Same product, same name. Rounded and smooth, so it stays loose — good for paths and play areas, wrong for anything you drive on.',
  },
  {
    national: ['Decomposed granite', 'DG', 'Fines path material'],
    localSlug: '3-8-minus-driveway',
    note: 'There is no local DG — it is a Southwest material. The closest thing here is walkway gravel, a 3/8" minus that packs into the same tight path surface.',
  },
  {
    national: ['Screened loam', 'Garden soil', 'Topsoil blend'],
    localSlug: '3-way-topsoil',
    note: 'Screened soil, sand, and compost blended three ways — the regional lawn and bed mix. Straight "topsoil" is rarely sold on its own here.',
  },
  {
    national: ['Hardwood mulch', 'Wood chips', 'Mulch'],
    localSlug: 'medium-bark',
    note: 'Around Puget Sound "mulch" means bark — fir and hemlock, not hardwood. A medium grind holds on slopes through winter rain.',
  },
  {
    national: ['Rip rap', 'Armor stone', 'Ballast rock'],
    localSlug: '4-8-quarry-spalls',
    note: 'Large angular rock for erosion control, mud bridging, and slope armour. Sold here by the spall size rather than a rip-rap class.',
  },
  {
    national: ['Bank run', 'Fill gravel', 'Unscreened gravel'],
    localSlug: 'pit-run',
    note: 'Unscreened material straight from the pit, mixed sizes. A cheap working platform over mud — cap it with minus rock, never leave it as a finished surface.',
  },
]
